import React from 'react';
import { Input, Button, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { BonusCodeOption } from '../../../services/pw9337Api';

interface QueryParamsProps {
  bonusCode: string;
  empFrom: string;
  empTo: string;
  deptFrom: string;
  deptTo: string;
  deptLevel: number;
  companyType: number;
  thanks: string;
  loading: boolean;
  bonusCodes: BonusCodeOption[];
  bonusCodesLoading: boolean;
  onBonusCodeChange: (v: string) => void;
  onEmpFromChange: (v: string) => void;
  onEmpToChange: (v: string) => void;
  onDeptFromChange: (v: string) => void;
  onDeptToChange: (v: string) => void;
  onDeptLevelChange: (v: number) => void;
  onCompanyTypeChange: (v: number) => void;
  onThanksChange: (v: string) => void;
  onQuery: () => void;
}

const deptLevelOptions = [
  { value: 1, label: '1-部' },
  { value: 2, label: '2-處' },
  { value: 3, label: '3-課' },
  { value: 4, label: '4-組' },
];

const companyTypeOptions = [
  { value: 1, label: '1-台北渤揚' },
  { value: 2, label: '2-昆山渤揚' },
  { value: 3, label: '3-全部' },
];

const QueryParams: React.FC<QueryParamsProps> = ({
  bonusCode, empFrom, empTo, deptFrom, deptTo, deptLevel, companyType, thanks,
  loading, bonusCodes, bonusCodesLoading,
  onBonusCodeChange, onEmpFromChange, onEmpToChange,
  onDeptFromChange, onDeptToChange, onDeptLevelChange, onCompanyTypeChange,
  onThanksChange, onQuery,
}) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Department range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>部門代號[從]</span>
          <Input
            value={deptFrom}
            onChange={(e) => onDeptFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 80 }}
            maxLength={4}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={deptTo}
            onChange={(e) => onDeptToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 80 }}
            maxLength={4}
          />
        </div>
      </div>

      {/* Row 2: Employee range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>人員代號[從]</span>
          <Input
            value={empFrom}
            onChange={(e) => onEmpFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 120 }}
            maxLength={8}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={empTo}
            onChange={(e) => onEmpToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 120 }}
            maxLength={8}
          />
        </div>
      </div>

      {/* Row 3: Bonus code */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>獎金代號</span>
          <Select
            value={bonusCode || undefined}
            onChange={onBonusCodeChange}
            loading={bonusCodesLoading}
            placeholder="請選擇獎金代號"
            style={{ width: 280 }}
            showSearch
            optionFilterProp="label"
            options={bonusCodes.map((bc) => ({
              value: bc.code,
              label: `${bc.code} ${bc.name} ${bc.date || ''}`.trim(),
            }))}
          />
        </div>
      </div>

      {/* Row 4: Thanks message */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>感謝詞</span>
          <Input
            value={thanks}
            onChange={(e) => onThanksChange(e.target.value)}
            style={{ width: 340 }}
            maxLength={100}
          />
        </div>
      </div>

      {/* Row 5: Department level + Company type */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 96 }}>部門分級</span>
          <Select
            value={deptLevel}
            onChange={onDeptLevelChange}
            style={{ width: 120 }}
            options={deptLevelOptions}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 72 }}>公司分級</span>
          <Select
            value={companyType}
            onChange={onCompanyTypeChange}
            style={{ width: 150 }}
            options={companyTypeOptions}
          />
        </div>
      </div>

      {/* Query button */}
      <div className="flex gap-2">
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={onQuery}
          loading={loading}
          disabled={!bonusCode}
        >
          查詢
        </Button>
      </div>
    </div>
  );
};

export default QueryParams;
